import type { Vessel, VesselRegistration, VesselWithStandardizedVoyagesResponse } from '@/types';
import { apiClient } from './apiClient';

interface CreateVesselResponse {
  message: string;
  vesselId: string;
}
interface DeleteVesselResponse {
  message: string;
}

export class VesselService {
  /**
   * Get all vessels for the (possibly impersonated) user
   */
  static async getVessels(userId?: string | null): Promise<Vessel[]> {
    const qs = userId ? `?userId=${encodeURIComponent(userId)}` : '';
    return apiClient.get<Vessel[]>(`/vessels${qs}`);
  }

  /**
   * Get a specific vessel by ID with its voyages
   */
  static async getVesselById(vesselId: string): Promise<VesselWithStandardizedVoyagesResponse> {
    return apiClient.get<VesselWithStandardizedVoyagesResponse>(`/vessels/${vesselId}`);
  }

  /**
   * Create a new vessel
   */
  static async createVessel(
    vessel: VesselRegistration,
    userId?: string | null
  ): Promise<CreateVesselResponse> {
    const qs = userId ? `?userId=${encodeURIComponent(userId)}` : '';
    return apiClient.post<CreateVesselResponse>(`/vessels${qs}`, vessel);
  }

  /**
   * Allocate EUA to a vessel
   */
  static async allocateEua(vesselId: string, amount: number): Promise<void> {
    return apiClient.put<void>(`/vessels/${vesselId}/allocate`, { amount });
  }

  /**
   * Deallocate EUA from a vessel
   */
  static async deallocateEua(vesselId: string, amount: number): Promise<void> {
    return apiClient.put<void>(`/vessels/${vesselId}/deallocate`, { amount });
  }

  /**
   * Delete a vessel
   */
  static async deleteVessel(
    vesselId: string,
    userId?: string | null
  ): Promise<DeleteVesselResponse> {
    const qs = userId ? `?userId=${encodeURIComponent(userId)}` : '';
    return apiClient.delete<DeleteVesselResponse>(`/vessels/${vesselId}${qs}`);
  }

  /**
   * Update vessel fields
   */
  static async updateVessel(
    vesselId: string,
    updates: Partial<
      Pick<Vessel, 'VesselName' | 'RegistrationStatus' | 'EuaPosition' | 'FleetManager'>
    >,
    userId?: string | null
  ): Promise<{ message: string }> {
    const qs = userId ? `?userId=${encodeURIComponent(userId)}` : '';
    return apiClient.put<{ message: string }>(`/vessels/${vesselId}${qs}`, updates);
  }

  /**
   * Upload (or replace) a vessel image
   */
  static async uploadVesselImage(
    vesselId: string,
    file: File,
    userId?: string | null
  ): Promise<{ ImageURL: string; ImagePath: string }> {
    const qs = userId ? `?userId=${encodeURIComponent(userId)}` : '';
    const formData = new FormData();
    formData.append('image', file);
    return apiClient.postFormData<{ ImageURL: string; ImagePath: string }>(
      `/vessels/${vesselId}/image${qs}`,
      formData
    );
  }

  /**
   * Delete a vessel image
   */
  static async deleteVesselImage(vesselId: string, userId?: string | null): Promise<void> {
    const qs = userId ? `?userId=${encodeURIComponent(userId)}` : '';
    return apiClient.delete<void>(`/vessels/${vesselId}/image${qs}`);
  }
}
